import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupThumbnailHover(signal);
    this.#setupKeyboard(signal);
    this.#setupStripFollow(signal);
  }

  /**
   * Pás miniatur jede za fotkou i při posunu touchpadem / swipem (2026-10-20).
   *
   * Horizon dorovná pás jen po kliknutí, tažení myší a šipkách
   * (`#centerSelectedThumbnail` v slideshow.js). Když se fotky posouvají
   * nativním scrollem — dvěma prsty na touchpadu nebo swipem na telefonu —
   * slideshow jen přepne zvýraznění miniatury (událost `slideshow:select`
   * s `trigger: 'scroll'`) a pás zůstane stát, takže aktivní miniatura
   * odjede mimo obraz. Naměřeno na FL7 (17 fotek): fotka 14, pás pořád na
   * začátku.
   *
   * KLIDNĚ: první verze pás posouvala při každé přepnuté fotce, takže při
   * delším posunu poskakoval po kouscích (Karlovi se to nelíbilo). Teď se
   * čeká, až se fotky dosunou (KLID_MS bez další změny), a teprve pak jede
   * pás JEDNOU plynulou animací (POSUN_MS, ease-in-out) tak, aby aktivní
   * miniatura byla uprostřed. Když je miniatura celá vidět, pás se nehne.
   *
   * Reaguje JEN na `trigger: 'scroll'`. Výběr najetím kurzorem na miniaturu
   * (`#setupThumbnailHover`) pás záměrně nehýbe a jde přes `select()`, tedy
   * `trigger: 'select'` — sem nespadne.
   *
   * @param {AbortSignal} signal
   */
  #setupStripFollow(signal) {
    const KLID_MS = 220;
    const POSUN_MS = 480;
    let cekani = null;
    let anim = 0;

    const strip = () =>
      this.querySelector('.slideshow-controls__thumbnails-container') ??
      this.querySelector('.slideshow-controls__thumbnails');

    const dorovnej = (index) => {
      const pas = strip();
      const thumb = this.querySelectorAll('.slideshow-controls__thumbnail')[index];
      if (!(pas instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;

      const sr = pas.getBoundingClientRect();
      const tr = thumb.getBoundingClientRect();
      if (tr.left >= sr.left && tr.right <= sr.right) return; // celá vidět, nehýbat

      const max = pas.scrollWidth - pas.clientWidth;
      const od = pas.scrollLeft;
      const kam = Math.max(0, Math.min(max, od + (tr.left + tr.width / 2) - (sr.left + sr.width / 2)));
      if (Math.abs(kam - od) < 1) return;

      cancelAnimationFrame(anim);
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        pas.scrollLeft = kam;
        return;
      }
      const t0 = performance.now();
      const krok = (t) => {
        const p = Math.min(1, (t - t0) / POSUN_MS);
        const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2; // ease-in-out
        pas.scrollLeft = od + (kam - od) * e;
        if (p < 1) anim = requestAnimationFrame(krok);
      };
      anim = requestAnimationFrame(krok);
    };

    this.addEventListener(
      'slideshow:select',
      (event) => {
        const detail = /** @type {any} */ (event).detail;
        if (!detail || detail.trigger !== 'scroll') return;
        if (event.target !== this.slideshow) return; // ne slideshow v okně galerie (zoom)
        clearTimeout(cekani);
        cekani = setTimeout(() => dorovnej(detail.index), KLID_MS);
      },
      { signal }
    );
  }

  /**
   * Šipky ←/→ přepínají fotky v galerii na stránce (2026-10-09). Horizon to
   * neuměl vůbec. Reaguje jen, když je nad galerií kurzor nebo v ní fokus —
   * jinak by šipky na produktové stránce zabíraly i běžné posouvání.
   * Když je otevřené okno galerie (zoom-dialog), nechává klávesy jemu.
   * Volá stejné next/previous jako tlačítka se šipkami, takže platí i
   * nekonečné procházení (z poslední fotky na první).
   *
   * @param {AbortSignal} signal
   */
  #setupKeyboard(signal) {
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const t = event.target;
        if (t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
        if (this.querySelector('dialog[open]')) return;
        if (!this.contains(document.activeElement) && !this.matches(':hover')) return;
        const slideshow = this.slideshow;
        if (!slideshow) return;
        event.preventDefault();
        if (event.key === 'ArrowRight') slideshow.next(event);
        else slideshow.previous(event);
      },
      { signal }
    );
  }

  /**
   * Alza-styl: na zařízeních s hoverem (desktop / myš) přepne hlavní fotku hned při
   * najetí kurzorem na miniaturu — nečeká se na klik. Na dotykových zařízeních se nic
   * nemění (tam funguje původní klik). Posluchače uklidí AbortController signal.
   *
   * @param {AbortSignal} signal
   */
  #setupThumbnailHover(signal) {
    if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;

    const thumbs = [...this.querySelectorAll('.slideshow-controls__thumbnail')];
    const strip =
      this.querySelector('.slideshow-controls__thumbnails-container') ??
      this.querySelector('.slideshow-controls__thumbnails');
    if (!thumbs.length) return;

    /* Pás miniatur se ovládá kurzorem po vzoru alza.cz. Dvě věci, které spolu
       NESMÍ souviset — na tom ztroskotaly předchozí pokusy:

       A) VÝBĚR FOTKY dělá jen setrvání kurzoru na miniatuře (180 ms).
          Výběr sám pásem NEHÝBE. Dřív hýbal, a tím si pod kurzor natlačil
          další miniaturu, ta se vybrala, pás se posunul zas — galerie ujela
          o několik fotek naráz. Prodleva je zároveň to, co je vidět na Alze:
          velká fotka nahoře naskočí s malým zpožděním, ne okamžitě, takže
          přejetí pásu kurzorem nerozbliká celou stránku.
          Odjetím z miniatury se čekání ruší, takže co kurzor jen přeletí,
          se nikdy nevybere.

       B) POSUN PÁSU dělá jen setrvání kurzoru v krajní zóně (56 px), a to
          přesně o jednu miniaturu za 320 ms. Ne o „kolik je potřeba", ne
          vycentrování — o jednu rozteč, spočítanou z odstupu prvních dvou
          miniatur (tedy včetně mezery mezi nimi).
          Bez toho se na první a poslední fotku kurzorem nedá dostat, myš na
          okraji pásu nemá kam pokračovat.

       V krajní zóně se pravidlo A vypíná a tempo řídí krokování (sekce B) —
       jinak by se čekání na výběr pořád restartovalo podjíždějícími
       miniaturami a nikdy nedoběhlo. */
    const PRODLEVA = 180;
    const ZONA = 56;

    const klid = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    /** Rozteč miniatur = šířka jedné včetně mezery za ní. */
    const roztec = () => {
      if (thumbs.length > 1) {
        const d = thumbs[1].offsetLeft - thumbs[0].offsetLeft;
        if (d > 0) return d;
      }
      return thumbs[0].offsetWidth || 80;
    };

    // ── A) výběr se zpožděním ───────────────────────────────────────────────
    let cekani = null;
    const zrusCekani = () => {
      if (cekani) clearTimeout(cekani);
      cekani = null;
    };

    const vyber = (index) => {
      if (index < 0 || index >= thumbs.length) return;
      const sl = this.slideshow;
      if (!sl) return;

      /* `select()` volá uvnitř `#centerSelectedThumbnail()`, což pás dorovná
         tak, aby byla vybraná miniatura uprostřed — skok přes několik fotek,
         přesně to, co tady nechceme.

         Zapamatovat si polohu a po `select()` ji vrátit NESTAČÍ: centrování
         jede přes `scrollIntoView`, které posun odkládá, takže naše vrácení
         proběhne dřív, než se pás vůbec pohne, a nemá co vracet. (Přesně na
         tomhle ztroskotaly předchozí pokusy — vypadalo to opravené jen
         u fotek, které byly vidět a centrovat se nepotřebovaly.)

         Proto se centrování rovnou nepustí ke slovu. `#centerSelectedThumbnail`
         se na začátku ptá na `this.refs.thumbnailsContainer` a bez něj se
         hned vrátí. `refs` je obyčejný objekt, který si komponenta drží
         nacachovaný (přepočítá ho MutationObserver, tedy až v mikrotasku),
         takže ho můžeme na dobu jednoho synchronního volání podstrčit
         prázdný a hned vrátit. Klik na miniaturu, tažení i šipky si tím
         pádem centrování zachovají — vypnuté je jen pro najetí kurzorem. */
      /* Prolnutí velké fotky (oq-media-fade v outeq-custom.css) se zapíná JEN
         tady, na chvíli kolem výběru z miniatury: zakrývá okamžitý skok
         `animate: false`. Při posunu pásu touchpadem, swipem nebo šipkami se
         fotka posouvá plynule sama a prolnutí ji jen shazovalo na 45 %
         a znovu rozsvěcovalo — to bylo to problikávání (2026-10-07). */
      this.dataset.oqFade = '';
      clearTimeout(this._oqFadeTimer);
      this._oqFadeTimer = setTimeout(() => delete this.dataset.oqFade, 400);

      const refs = sl.refs;
      const puvodni = refs ? refs.thumbnailsContainer : undefined;
      if (refs) refs.thumbnailsContainer = undefined;
      try {
        sl.select(index, undefined, { animate: false });
      } finally {
        if (refs) refs.thumbnailsContainer = puvodni;
      }
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener(
        'mouseenter',
        () => {
          if (krokTimer) return; // v krajní zóně si tempo řídí krokování samo
          zrusCekani();
          cekani = setTimeout(() => vyber(index), PRODLEVA);
        },
        { signal }
      );
      thumb.addEventListener('mouseleave', zrusCekani, { signal });
    });

    if (!strip) return;

    // ── B) krokování v krajních zónách ──────────────────────────────────────
    /* Tady tempo udává VÝBĚR, ne čekání na kurzor. Předtím to bylo obráceně
       a rozbíjelo se to: kurzor v krajní zóně stojí na místě, pás se pod ním
       posouvá, každá podjíždějící miniatura vyvolá `mouseenter` a tím
       restartuje 180ms čekání na výběr — jenže pauza mezi kroky je kratší
       (140 ms), takže čekání nikdy nedoběhlo. Výběr zamrzl a naskočil až
       potom, co se pás zastavil na konci, rovnou o několik fotek dál.
       Naměřeno na notebooku 0 → 6, na monitoru 0 → 9.
       Proč se to projevilo až teď: na širokém monitoru je odrolovat jen
       ~90 px, na notebooku 338 px, takže tam kurzor v krajní zóně tráví
       mnohem víc času a chyba se stihne nasčítat.

       Teď se každých KROK_MS posune výběr o jednu fotku a pás se za ním
       dorovná. Nikdy se tedy nepřeskočí, ať se pás posouvá jakkoli dlouho.

       DOJEZD se týká jen POSUNU PÁSU, ne výběru: když na danou stranu zbývají
       schované nejvýš dvě fotky, pás dojede rovnou na doraz místo dvou
       samostatných cuknutí. Výběr i tehdy jde po jedné. */
    const KROK_MS = 300;
    const POSUN_MS = 220;
    const DOJEZD_FOTEK = 2;

    let vZone = 0; // -1 vlevo, 1 vpravo, 0 mimo
    let krokTimer = null;

    const vybrany = () => {
      const i = thumbs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      return i === -1 ? 0 : i;
    };

    /** Vlastní animace posunu — `scrollTo({behavior:'smooth'})` má délku,
     *  kterou si prohlížeč určuje sám, a u krátkých vzdáleností vycházela
     *  skoro stejně dlouhá jako perioda krokování; kroky se pak překrývaly
     *  a pás místo krokování souvisle plul. */
    const animujPas = (kam) => {
      const od = strip.scrollLeft;
      if (Math.abs(kam - od) < 1) return;
      if (klid()) {
        strip.scrollLeft = kam;
        return;
      }
      const t0 = performance.now();
      const f = (t) => {
        const p = Math.min(1, (t - t0) / POSUN_MS);
        strip.scrollLeft = od + (kam - od) * (1 - Math.pow(1 - p, 3));
        if (p < 1) requestAnimationFrame(f);
        else strip.scrollLeft = kam;
      };
      requestAnimationFrame(f);
    };

    /** Dorovná pás tak, aby byla vidět další miniatura ve směru pohybu. */
    const dorovnej = (smer) => {
      const max = strip.scrollWidth - strip.clientWidth;
      const od = strip.scrollLeft;
      const zbyva = smer > 0 ? max - od : od;
      if (zbyva < 1) return;
      const cil =
        zbyva <= DOJEZD_FOTEK * roztec() + 2
          ? smer > 0
            ? max
            : 0
          : Math.max(0, Math.min(max, od + smer * roztec()));
      animujPas(cil);
    };

    const zastav = () => {
      vZone = 0;
      if (krokTimer) clearInterval(krokTimer);
      krokTimer = null;
    };

    const krok = () => {
      const smer = vZone;
      if (!smer) return;
      const dalsi = vybrany() + smer;
      if (dalsi < 0 || dalsi >= thumbs.length) {
        zastav();
        return;
      }
      vyber(dalsi);
      dorovnej(smer);
    };

    const spust = (smer) => {
      if (krokTimer && vZone === smer) return;
      zastav();
      zrusCekani(); // ať do krokování nezasáhne rozpracované čekání z hoveru
      vZone = smer;
      krok();
      krokTimer = setInterval(krok, KROK_MS);
    };

    strip.addEventListener(
      'pointermove',
      (event) => {
        /* Krokuje se podle toho, jestli zbývají FOTKY, ne jestli se dá ještě
           rolovat. Dřív to bylo navázané na `scrollLeft`, a když pás dojel na
           doraz, krokování se zastavilo — jenže poslední miniatura leží právě
           v krajní zóně a nehybný kurzor nad ní už žádnou událost nedostane,
           takže se na první a poslední fotku nedalo dostat (dojelo to na 8
           z 9 a zpátky jen na 1). Pás prostě na konci stojí a mění se dál
           jen výběr. */
        const sr = strip.getBoundingClientRect();
        if (event.clientX > sr.right - ZONA) spust(1);
        else if (event.clientX < sr.left + ZONA) spust(-1);
        else zastav();
      },
      { signal }
    );

    strip.addEventListener(
      'pointerleave',
      () => {
        zastav();
        zrusCekani();
      },
      { signal }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
